// frontend/src/pages/admin/lcl/tabs/PrelevementsTab.jsx
import { useMemo, useState } from 'react';
import { FiSearch, FiDownload, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { useLclPrelevements } from '../hooks/useLclPrelevements';
import { useLclCollisions } from '../hooks/useLclCollisions';
import MaskedIban from '../components/MaskedIban';
import ExportConfirmModal from '../components/ExportConfirmModal';
import CollisionResolverModal from '../components/CollisionResolverModal';
import { montantTtc } from '../utils/montantTtc';

const fmt = (n) =>
  Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

export default function PrelevementsTab() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [exported, setExported] = useState('false');
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [selected, setSelected] = useState({});
  const [resolver, setResolver] = useState(null);  // null OR Array<collision>

  const { data, loading, refetch, exportCsv } = useLclPrelevements({ date_reglement: date, exported });
  const { data: collisions, refetch: refetchCollisions } = useLclCollisions(date);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return data;
    return data.filter((d) =>
      d.accounts?.company_name?.toLowerCase().includes(s) ||
      d.numero_facture?.toLowerCase().includes(s) ||
      d.accounts?.sepa_mandate_rum?.toLowerCase().includes(s)
    );
  }, [data, search]);

  const selectable   = filtered.filter((d) => !d.lcl_csv_exported_at);
  const selectedIds  = selectable.filter((d) => selected[d.id]).map((d) => d.id);

  // Les colonnes "Montant" affichent le TTC (= montant prélevé via SEPA),
  // pas le HT stocké en BDD. Cohérent avec la facture envoyée au client.
  const total = useMemo(() => selectable
    .filter((d) => selected[d.id])
    .reduce((s, d) => s + montantTtc(d.montant_facture), 0), [selected, selectable]);

  const totalAll = useMemo(() =>
    filtered.reduce((s, d) => s + montantTtc(d.montant_facture), 0), [filtered]);

  const toggleAll = () => {
    if (selectedIds.length === selectable.length) setSelected({});
    else setSelected(Object.fromEntries(selectable.map((d) => [d.id, true])));
  };

  const checked = selectable.length > 0 && selectedIds.length === selectable.length;
  const indeterminate = selectedIds.length > 0 && selectedIds.length < selectable.length;

  // Filtrer les collisions qui touchent la sélection (compte à compte)
  const filteredCollisions = collisions.filter(
    (c) => c.items.every((x) => selected[x.id])
  );

  async function startExport() {
    if (filteredCollisions.length > 0) {
      setResolver(filteredCollisions);
    } else {
      setConfirm(true);
    }
  }

  async function doExport(strategy = 'none') {
    const ids = selectedIds.slice();
    setConfirm(false); setResolver(null); setSelected({});
    await exportCsv(ids, strategy);
    await refetchCollisions();
  }

  return (
    <div className="space-y-3">

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Prélèvements à exporter</div>
          <div className="text-base font-bold text-blue-700">{selectable.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Sélectionnés</div>
          <div className="text-base font-bold text-green-700">{selectedIds.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] text-gray-500 font-medium">Total sélection</div>
          <div className="text-base font-bold text-purple-700">{fmt(total)}</div>
        </div>
      </div>

      {/* Bandeau action */}
      <div className="bg-white rounded shadow border border-gray-200 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-gray-500">
          Générer le CSV LCL pour la date de règlement sélectionnée — Total affichage : {fmt(totalAll)}
        </div>
        <button
          onClick={startExport}
          disabled={selectedIds.length === 0}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold text-white
                     bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          <FiDownload className="h-3.5 w-3.5" />
          Générer CSV ({selectedIds.length})
          {filteredCollisions.length > 0 && (
            <span className="ml-1 text-orange-200">
              — {filteredCollisions.length} collision{filteredCollisions.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded border border-gray-200 p-2 flex flex-wrap items-center gap-2">

        <div className="relative">
          <FiCalendar className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5 pointer-events-none" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="pl-7 pr-2 py-1 text-[11px] border border-gray-200 rounded
                       focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            title="Date de règlement"
          />
        </div>

        <div className="relative flex-1 min-w-[180px]">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Client, facture, RUM…"
            className="w-full pl-7 pr-2 py-1 text-[11px] border border-gray-200 rounded
                       focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="inline-flex rounded border border-gray-200 overflow-hidden">
          {[
            { k: 'false', l: 'Non exportés' },
            { k: 'true', l: 'Exportés' },
            { k: '', l: 'Tous' },
          ].map(({ k, l }) => (
            <button
              key={k}
              onClick={() => setExported(k)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors ${
                exported === k ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <button
          onClick={refetch}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
        >
          <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-xs text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-xs text-gray-400">
            Aucun prélèvement pour cette date.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={checked}
                      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                      onChange={toggleAll}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  {['Client', 'Facture', 'Commission TTC', 'RUM', 'IBAN', 'Exporté']
                    .map((h) => (
                      <th key={h}
                          className="px-3 py-2 text-left text-[10px] font-semibold
                                     text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((d) => {
                  const disabled = !!d.lcl_csv_exported_at;
                  return (
                    <tr key={d.id} className={`hover:bg-gray-50 ${disabled ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={!!selected[d.id]}
                          onChange={() => setSelected((s) => ({ ...s, [d.id]: !s[d.id] }))}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-[11px] font-semibold text-gray-900">
                          {d.accounts?.company_name || '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-[11px] font-medium text-gray-800">
                          {d.numero_facture || '—'}
                        </div>
                        <div className="text-[10px] text-gray-400">
                          Exercice {d.exercice_annee}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] font-bold text-green-700" title="Montant TTC prélevé (HT × 1.20)">
                        {fmt(montantTtc(d.montant_facture))}
                        <span className="ml-1 text-[9px] font-normal text-gray-400">TTC</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500 font-mono">
                        {d.accounts?.sepa_mandate_rum || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px]">
                        <MaskedIban iban={d.accounts?.iban} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {disabled ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                            {fmtDate(d.lcl_csv_exported_at)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CollisionResolverModal
        open={!!resolver}
        collisions={resolver || []}
        onClose={() => setResolver(null)}
        onConfirm={(strategy) => doExport(strategy)}
      />

      <ExportConfirmModal
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={() => doExport('none')}
        title="Générer le fichier CSV LCL"
        description={
          <>
            <div className="mb-1">
              <strong className="text-green-700">{selectedIds.length} prélèvement(s)</strong>
              {' '}pour un total de{' '}
              <strong className="text-purple-700">{fmt(total)}</strong>.
            </div>
            <div className="text-gray-500 text-[10px]">
              Date de règlement : {fmtDate(date)} — Ils ne pourront plus être exportés à nouveau.
            </div>
          </>
        }
        confirmLabel="Générer"
        confirmColor="green"
      />
    </div>
  );
}
